import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { format } from 'date-fns';
import { later } from 'lib/timers';
import { createRunPoller } from 'lib/ai-optimization';
import { SPEND_POLL_MS, INITIAL_SPEND_STATE, nextSpendState, spendView } from 'lib/openai-spend';
import { getOpenAiSpend } from 'api/trading-api';
import { CircleExclamationIcon } from 'components/icons';

// Local time of the as-of tooltip; a bad string from the server gives '?' and never throws
const formatTime = iso => {
	const date = new Date(iso);
	return Number.isNaN(date.getTime()) ? '?' : format(date, 'HH:mm');
};

// What OpenAI costs today and this month, for the admin header. This component owns the poll timer (the header returns
// null before it mounts, so it cannot): one load on mount, then every 5 min 30 s (just past the server cache), released
// on unmount. An answer that arrives after unmount or after a newer request is dropped. Trouble is shown as greyed
// amounts plus a yellow exclamation icon after the label with the reason on its own hover, never as an alert. The hover
// on the amounts tells only their age.
const OpenAiSpendDisplay = ({ className = '' }) => {
	const [state, setState] = useState(INITIAL_SPEND_STATE);
	const pollerRef = useRef(null);
	const loadRef = useRef(null);

	const load = async () => {
		const poller = pollerRef.current;
		if (!poller) {
			return;
		}
		const seq = poller.begin();
		const outcome = await getOpenAiSpend();
		if (!poller.isCurrent(seq)) {
			return;
		}
		setState(previous => nextSpendState(previous, outcome));
	};
	loadRef.current = load;

	useEffect(() => {
		const poller = createRunPoller({
			tick: () => loadRef.current(),
			schedule: later,
			intervalMs: SPEND_POLL_MS,
		});
		pollerRef.current = poller;
		loadRef.current();
		poller.sync(true);
		return () => {
			poller.stop();
		};
	}, []);

	const view = spendView(state, formatTime);

	let errorIcon;
	if (view.error) {
		errorIcon = (
			<span className="cursor-default" title={view.error} aria-label={view.error} role="img">
				<CircleExclamationIcon className="h-3 w-3 text-yellow-400" />
			</span>
		);
	}

	return (
		<div className={clsx('flex flex-col whitespace-nowrap cursor-default', className)}>
			<span className="flex items-center gap-1 text-xs text-gray-400 leading-none">
				{view.label}
				{errorIcon}
			</span>
			<span
				className={clsx('text-sm cursor-default', view.stale ? 'text-gray-500' : 'text-gray-300')}
				title={view.tooltip}
			>
				{view.text}
			</span>
		</div>
	);
};

OpenAiSpendDisplay.propTypes = {
	className: PropTypes.string,
};

export default OpenAiSpendDisplay;
